'use client'

import { Fragment, useTransition } from 'react'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
} from '@/components/ui/select'
import { LOCALE_TRANSLATION } from '@/constants/i18n.constants'
import { useRouter } from '@/i18n/navigation'
import type { AppLocale } from '@/types/general'
import type { LocaleLink } from '@/types/i18n.types'

type Props = {
	currentLocale: AppLocale
	localeLinks: LocaleLink[]
}

const LocaleSwitcher = ({ currentLocale, localeLinks }: Props) => {
	// prevent the locale switch from blocking the UI thread
	const [, startTransition] = useTransition()
	const router = useRouter()

	const handleLocaleChange = (newLocale: string) => {
		const link = localeLinks.find((l) => l.locale === newLocale)
		if (!link) return

		startTransition(() => {
			// localeLinks are derived from typed routes; cast bridges router.replace typing.
			router.replace(link.pathname as Parameters<typeof router.replace>[0], { locale: newLocale })
		})
	}

	return (
		<Select value={currentLocale} onValueChange={handleLocaleChange}>
			<SelectTrigger className="w-32 font-bold uppercase bg-white border-brand-black transition-colors cursor-pointer">
				{currentLocale}
			</SelectTrigger>
			<SelectContent className="rounded-sm border-none shadow-box">
				{localeLinks.map((link, index) => (
					<Fragment key={link.locale}>
						<SelectItem value={link.locale} className="cursor-pointer">
							{LOCALE_TRANSLATION[link.locale as keyof typeof LOCALE_TRANSLATION]}
						</SelectItem>
						{index < localeLinks.length - 1 && <SelectSeparator key={`${link.locale}-separator`} />}
					</Fragment>
				))}
			</SelectContent>
		</Select>
	)
}

export default LocaleSwitcher
